import { useCallback, useEffect, useMemo, useRef, useState, RefObject } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { GET_POSTS } from '@/modules/API/news/news'
import { GET_NEWS_NOT_VIEWED } from '@/modules/API/parent/parentNewsHandler'
import { Post, PostList } from '@/types/NewsFeed'
import { Role } from '@/types/Role'
import { useNotification } from '@/modules/context/NotificationContext'
import customEventHandler from '@/modules/utils/customEventHandler'

interface UseFeedDataProps {
    daycareId: number
    role: Role
    isDraft: boolean
    categoryIds: number[]
    sectionIds?: number[]
    childIds?: number[]
    withoutSection?: boolean
    onDraftCountChange?: (count: number) => void
}

interface UseFeedDataReturn {
    sortedPosts: Post[]
    hasMorePosts: boolean
    countDraftPosts: number
    isLoading: boolean
    isLoadingMore: boolean
    isRefetching: boolean
    error: boolean
    hasData: boolean
    loadedDaycareId: number
    loadMore: () => Promise<void>
    refetch: () => Promise<void>
}

interface getPostQueryVariable {
    childIds?: number[]
    sectionIds?: number[]
    withoutSection?: boolean
    daycareId: number
    skip: number
    draft: boolean
    categoryIds: number[]
}

const EMPTY_POST_LIST: PostList = {
    posts: [],
    hasMorePosts: false,
    countDraftPosts: 0
}

export function useFeedData({
    daycareId,
    role,
    isDraft,
    sectionIds = [],
    childIds = [],
    withoutSection = false,
    onDraftCountChange,
    categoryIds = []
}: UseFeedDataProps): UseFeedDataReturn {
    const { t } = useTranslation()
    const { showNotificationError } = useNotification()

    const [feedList, setFeedList] = useState<PostList>(EMPTY_POST_LIST)
    const [loadedDaycareId, setLoadedDaycareId] = useState<number>(0)
    const [isRefetching, setIsRefetching] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)

    const [getAllPosts, { loading: isLoading, error: queryError, data: queryData, refetch: refetchPosts }] =
        useLazyQuery<{ getPosts: PostList }>(GET_POSTS)

    const [getNewsNotViewed, { data: newsNotViewedData }] = useLazyQuery(GET_NEWS_NOT_VIEWED)

    const sortedPosts: Post[] = useMemo((): Post[] => {
        if (!feedList?.posts) return []

        return [...feedList.posts].sort((a: Post, b: Post): number => {
            const dateA: Date = isDraft ? new Date(a.creationDate) : new Date(a.updateDate)
            const dateB: Date = isDraft ? new Date(b.creationDate) : new Date(b.updateDate)
            return dateB.getTime() - dateA.getTime()
        })
    }, [feedList?.posts, isDraft])

    const buildQueryVariables: (skip: number) => getPostQueryVariable = useCallback(
        (skip: number): getPostQueryVariable => {
            const baseVars: getPostQueryVariable = {
                daycareId,
                skip,
                draft: isDraft,
                categoryIds
            }

            if (isDraft) {
                return baseVars
            }

            return {
                ...baseVars,
                childIds: role === Role.parent ? childIds : [],
                sectionIds: role === Role.staff ? sectionIds : [],
                withoutSection
            }
        },
        [daycareId, isDraft, role, childIds, sectionIds, withoutSection, categoryIds]
    )

    useEffect(() => {
        setFeedList(EMPTY_POST_LIST)
        setLoadedDaycareId(0)
        let cancelled: boolean = false

        const fetchInitialData: () => Promise<void> = async (): Promise<void> => {
            const { data } = await getAllPosts({
                variables: buildQueryVariables(0)
            })

            if (!cancelled && data) {
                setFeedList(data.getPosts)
                setLoadedDaycareId(daycareId)
            }

            if (!cancelled && !isDraft && role === Role.parent) {
                await getNewsNotViewed()
            }
        }

        fetchInitialData()

        return () => {
            cancelled = true
        }
    }, [daycareId, isDraft, ...(isDraft ? [] : [childIds, sectionIds])])

    const previousCategoryIdsRef: RefObject<number[] | null> = useRef<number[] | null>(null)

    useEffect(() => {
        if (previousCategoryIdsRef.current === null) {
            previousCategoryIdsRef.current = categoryIds
            return
        }

        const previousCategoryIds: number[] = previousCategoryIdsRef.current
        const hasChanged: boolean =
            categoryIds.length !== previousCategoryIds.length ||
            categoryIds.some((id: number, index: number): boolean => id !== previousCategoryIds[index])

        if (!hasChanged) return
        previousCategoryIdsRef.current = categoryIds

        let cancelled: boolean = false

        const refetchForCategoryChange: () => Promise<void> = async (): Promise<void> => {
            try {
                const { data } = await refetchPosts(buildQueryVariables(0))

                if (!cancelled && data) {
                    setFeedList(data.getPosts)
                    setLoadedDaycareId(daycareId)
                }
            } catch {
                showNotificationError(t('generalError'))
            }
        }

        refetchForCategoryChange()

        return () => {
            cancelled = true
        }
    }, [categoryIds])

    useEffect(() => {
        if (newsNotViewedData) {
            customEventHandler.publish(newsNotViewedData.getNewsNotViewed)
        }
    }, [newsNotViewedData])

    useEffect(() => {
        if (onDraftCountChange) {
            onDraftCountChange(feedList.countDraftPosts)
        }
    }, [feedList.countDraftPosts, onDraftCountChange])

    const loadMore: () => Promise<void> = useCallback(async (): Promise<void> => {
        setIsLoadingMore(true)

        try {
            const { data } = await refetchPosts(buildQueryVariables(feedList.posts.length))

            if (data) {
                setFeedList(
                    (prev: PostList): PostList => ({
                        posts: [...prev.posts, ...data.getPosts.posts],
                        hasMorePosts: data.getPosts.hasMorePosts,
                        countDraftPosts: data.getPosts.countDraftPosts
                    })
                )

                if (!isDraft && role === Role.parent) {
                    await getNewsNotViewed()
                }
            }
        } catch {
            showNotificationError(t('generalError'))
        } finally {
            setIsLoadingMore(false)
        }
    }, [
        buildQueryVariables,
        feedList.posts.length,
        refetchPosts,
        isDraft,
        role,
        getNewsNotViewed,
        showNotificationError,
        t
    ])

    const refetch: () => Promise<void> = useCallback(async (): Promise<void> => {
        setIsRefetching(true)

        if (!isDraft) {
            const shouldFetch: boolean =
                role === Role.trustee ||
                role === Role.cald ||
                (role === Role.staff && sectionIds.length > 0) ||
                (role === Role.parent && childIds.length > 0)

            if (!shouldFetch) {
                setIsRefetching(false)
                return
            }
        }

        try {
            const { data } = await refetchPosts(buildQueryVariables(0))

            if (data) {
                setFeedList({
                    posts: data.getPosts.posts,
                    hasMorePosts: data.getPosts.hasMorePosts,
                    countDraftPosts: data.getPosts.countDraftPosts
                })
                setLoadedDaycareId(daycareId)

                if (!isDraft && role === Role.parent) {
                    await getNewsNotViewed()
                }
            } else {
                setFeedList(EMPTY_POST_LIST)
                setLoadedDaycareId(0)
            }
        } catch {
            setFeedList(EMPTY_POST_LIST)
            setLoadedDaycareId(0)
        } finally {
            setIsRefetching(false)
        }
    }, [buildQueryVariables, refetchPosts, isDraft, role, sectionIds.length, childIds.length, getNewsNotViewed])

    return {
        sortedPosts,
        hasMorePosts: feedList.hasMorePosts,
        countDraftPosts: feedList.countDraftPosts,
        isLoading,
        isLoadingMore,
        isRefetching,
        error: !!queryError,
        hasData: !!queryData,
        loadedDaycareId,
        loadMore,
        refetch
    }
}
